import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
  changeTools,
  changePriceVolumeParam,
  changeCycleAnaType,
  changeTenkankikan,
} from "redux/ducks/Chart"
import chartColors from "../logic/chartColor"
import ChartColorPicker from "./ChartColorPicker"
import ChartInputNumeric from "./ChartInputNumeric"
import { CycleAnaTypesValue, TOOLS, PLOTTYPE } from "../logic/chartConfig"
import { Labels } from "components/atoms/input/input"
import { Btn } from "components/atoms/btn"
import _ from "lodash"
import ClassNames from "classnames"
import { useChartTechToolTip } from "components/atoms/chart/parts/ChartTechToolTip"

interface props {
  canvasId: string
}

const MAGNIFICATION = [
  { displayTitle: "1/2", value: 0.5 },
  { displayTitle: "標準", value: 1.0 },
  { displayTitle: "X2", value: 2.0 },
]

const DISP_WIDTH_RATIO = {
  short: 0.2,
  long: 0.95,
}

const MAXDISP_COLORS = [
  { id: 0, displayTitle: "売買高", key: "PriceVolume" },
  { id: 1, displayTitle: "売買高(最大)", key: "PvMaxVolume" },
  { id: 2, displayTitle: "最大値値色", key: "PvMaxNum" },
  { id: 3, displayTitle: "最大値背景色", key: "PvMaxNumBack" },
]

const LOWDISP_COLORS = [
  { id: 0, displayTitle: "上向き", key: "CycleHigh" },
  { id: 1, displayTitle: "下向き", key: "CycleLow" },
  { id: 2, displayTitle: "上向き値", key: "CycleHighFont" },
  { id: 3, displayTitle: "下向き値", key: "CycleLowFont" },
]

const TENKANTEN_COLORS = [
  {
    id: 0,
    displayTitle: "転換の文字色",
    key: "Tenkan_Label",
  },
  { id: 1, displayTitle: "転換の塗りつぶし", key: "Tenkan_Fill" },
]

const TENKANTEN_MAX = 100

export const PriceVolumePropety = React.memo((props: props) => {
  const { canvasId } = props
  const dispatch = useDispatch()
  const priceVolumeParamSelector = (state: any) =>
    state.chart[canvasId].priceVolumeParam
  const plotTypeSelector = (state: any) => state.chart[props.canvasId].plotType
  const providerSelector = (state: any) => state.chart[props.canvasId].providers
  const colorSettingSelector = (state: any) => state.chart.colorSetting

  const plotType = useSelector(plotTypeSelector)
  const providers = useSelector(providerSelector)
  const colorSetting = useSelector(colorSettingSelector)
  const priceVolumeParam = useSelector(priceVolumeParamSelector)
  const [_provider, _controller, _procs] = providers

  const [selectedMagnificationId, setSelectedMagnificationId] = useState(
    (() => {
      let id = 0
      _.forEach(MAGNIFICATION, (items, index) => {
        if (items.value === priceVolumeParam.Ratio) id = index
      })
      return id
    })()
  )
  const [DispWidthRatio, setDispWidthRatio] = useState(
    priceVolumeParam.DispWidthRatio === DISP_WIDTH_RATIO.long
  )
  const [MaxDisp, setMaxDisp] = useState(priceVolumeParam.MaxDisp)

  useEffect(() => {
    const _priceVolumeParam = _.clone(priceVolumeParam)
    const value = MAGNIFICATION[selectedMagnificationId].value
    _.set(_priceVolumeParam, ["Ratio"], value)
    dispatch(changePriceVolumeParam(canvasId, _priceVolumeParam))
  }, [selectedMagnificationId])

  useEffect(() => {
    const _priceVolumeParam = _.clone(priceVolumeParam)
    const value = DispWidthRatio
      ? DISP_WIDTH_RATIO.long
      : DISP_WIDTH_RATIO.short
    _.set(_priceVolumeParam, ["DispWidthRatio"], value)
    dispatch(changePriceVolumeParam(canvasId, _priceVolumeParam))
  }, [DispWidthRatio])

  useEffect(() => {
    const _priceVolumeParam = _.clone(priceVolumeParam)
    _.set(_priceVolumeParam, ["MaxDisp"], MaxDisp)
    dispatch(changePriceVolumeParam(canvasId, _priceVolumeParam))
  }, [MaxDisp])

  useEffect(() => {
    chartColors.setColorPattern(plotType, colorSetting, _controller)
  }, [colorSetting])

  useEffect(() => {
    let id = 0
    _.forEach(MAGNIFICATION, (items, index) => {
      if (items.value === priceVolumeParam.Ratio) id = index
    })
    setSelectedMagnificationId(id)
    setDispWidthRatio(priceVolumeParam.DispWidthRatio === DISP_WIDTH_RATIO.long)
    setMaxDisp(priceVolumeParam.MaxDisp)
  }, [priceVolumeParam])

  return (
    <div className="chart-setting-options">
      <div className="chart-setting-options-item">
        <label className="checkbox-control">
          <input
            type="checkbox"
            onChange={() => setMaxDisp(!MaxDisp)}
            checked={MaxDisp}
          />
          <span>最大値表示</span>
        </label>
        <div className="chart-setting-colors">
          {_.map(MAXDISP_COLORS, items => {
            return (
              <div key={items.id} className="chart-setting-colors-item">
                <span className="chart-setting-colors-title">
                  {items.displayTitle}
                </span>
                <ChartColorPicker
                  type={"addon"}
                  targetGroup={"PriceVolume"}
                  target={items.key}
                  parentId={"tech-setting"}
                />
              </div>
            )
          })}
        </div>
      </div>
      <div className="chart-setting-options-item">
        <label className="checkbox-control">
          <input
            type="checkbox"
            onChange={() => setDispWidthRatio(!DispWidthRatio)}
            checked={DispWidthRatio}
          />
          <span>拡大</span>
        </label>
        <Labels
          className="btn-segmented-control btn-thin liquid-control"
          labelList={[
            { name: "1/2", id: 0 },
            { name: "標準", id: 1 },
            { name: "X2", id: 2 },
          ]}
          checked={selectedMagnificationId}
          onChange={setSelectedMagnificationId}
        />
      </div>
    </div>
  )
})

export const CycleAnaPropety = React.memo((props: props) => {
  const { canvasId } = props
  const dispatch = useDispatch()
  const cycleAnaTypesSelector = (state: any) =>
    state.chart[canvasId].cycleAnaTypes
  const plotTypeSelector = (state: any) => state.chart[props.canvasId].plotType
  const providerSelector = (state: any) => state.chart[props.canvasId].providers
  const colorSettingSelector = (state: any) => state.chart.colorSetting

  const plotType = useSelector(plotTypeSelector)
  const providers = useSelector(providerSelector)
  const colorSetting = useSelector(colorSettingSelector)
  const cycleAnaTypes = useSelector(cycleAnaTypesSelector)
  const [_provider, _controller, _procs] = providers

  const [selectedItems, setSelectedItems] = useState(
    (() => {
      let defaultItems: Array<number> = []
      _.forEach(cycleAnaTypes, items => defaultItems.push(items.id))
      return defaultItems
    })()
  )

  const setSelecteditem = (id: number) => {
    const _selectedItems = _.clone(selectedItems)
    if (_.includes(_selectedItems, id)) {
      _.pull(_selectedItems, id)
    } else {
      _selectedItems.push(id)
    }
    dispatch(changeCycleAnaType(canvasId, _selectedItems))
    setSelectedItems(_selectedItems)
  }

  useEffect(() => {
    chartColors.setColorPattern(plotType, colorSetting, _controller)
  }, [colorSetting])

  useEffect(() => {
    let _selectedItems: Array<number> = []
    _.forEach(cycleAnaTypes, items => _selectedItems.push(items.id))
    setSelectedItems(_selectedItems)
  }, [cycleAnaTypes])

  return (
    <div className="chart-setting-options">
      {_.map(CycleAnaTypesValue, items => {
        return (
          <div key={items.id} className="chart-setting-options-item">
            <label className="checkbox-control">
              <input
                type="checkbox"
                onChange={() => setSelecteditem(items.id)}
                checked={_.includes(selectedItems, items.id)}
              />
              <span>{items.displayTitle}</span>
            </label>
            <div className="chart-setting-colors">
              {items.id === 2 &&
                _.map(LOWDISP_COLORS, items => {
                  return (
                    <div key={items.id} className="chart-setting-colors-item">
                      <span className="chart-setting-colors-title">
                        {items.displayTitle}
                      </span>
                      <ChartColorPicker
                        type={"addon"}
                        targetGroup={"CycleAna"}
                        target={items.key}
                        parentId={"tech-setting"}
                      />
                    </div>
                  )
                })}
            </div>
          </div>
        )
      })}
    </div>
  )
})

export const TenkantenPropety = React.memo((props: props) => {
  const { canvasId } = props
  const dispatch = useDispatch()
  const colorSettingSelector = (state: any) => state.chart.colorSetting
  const plotTypeSelector = (state: any) => state.chart[props.canvasId].plotType
  const providerSelector = (state: any) => state.chart[props.canvasId].providers
  const tenkanKikanSelector = (state: any) => state.chart[canvasId].tenkanKikan

  const colorSetting = useSelector(colorSettingSelector)
  const plotType = useSelector(plotTypeSelector)
  const providers = useSelector(providerSelector)
  const tenkanKikan = useSelector(tenkanKikanSelector)
  const [_provider, _controller, _procs] = providers

  const _changeTenkankikan = useCallback(
    (value: number) => {
      dispatch(changeTenkankikan(canvasId, value))
    },
    [canvasId]
  )

  useEffect(() => {
    chartColors.setColorPattern(plotType, colorSetting, _controller)
  }, [colorSetting])

  return (
    <div className="chart-setting-options">
      <div className="chart-setting-options-item">
        <div className="chart-setting">
          <ChartInputNumeric
            max={TENKANTEN_MAX}
            step={1}
            min={2}
            defaultValue={tenkanKikan}
            decimalDigits={0}
            setValue={_changeTenkankikan}
          />
          <div className="chart-setting-colors">
            {_.map(TENKANTEN_COLORS, items => {
              return (
                <div key={items.id} className="chart-setting-colors-item">
                  <span className="chart-setting-colors-title">
                    {items.displayTitle}
                  </span>
                  <ChartColorPicker
                    type={"addon"}
                    targetGroup={"tenkan"}
                    target={items.key}
                    parentId={"tech-setting"}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
})

const ToolsPropety = React.memo((props: props) => {
  const { canvasId } = props
  const dispatch = useDispatch()
  const toolsSelector = (state: any) => state.chart[canvasId].tools
  const plotTypeSelector = (state: any) => state.chart[canvasId].plotType

  const tools = useSelector(toolsSelector)
  const plotType = useSelector(plotTypeSelector)

  const [selectedItems, setSelectedItems] = useState([])
  const [visibleList, setVisibleList] = useState([])
  const data = [TOOLS[1], TOOLS[5], TOOLS[6]]

  const PandFFlag = useMemo(() => {
    return plotType.id === PLOTTYPE[3].id
  }, [plotType])

  const [tooltipVisible, setTooltipVisible] = useState<number>(null)
  const { _setPosition, createToolTip } = useChartTechToolTip({ type: 2 })

  // F#側の処理は、ChartToolButtonsコンポーネントで監視し実行している。
  // そのため、Reduxに入れるだけでよい。
  const setSelect = (id: number) => {
    let _selectedItems = _.clone(selectedItems)
    if (_.includes(_selectedItems, id)) {
      _.pull(_selectedItems, id)
    } else {
      _selectedItems.push(id)
    }
    dispatch(changeTools(canvasId, _selectedItems))
    setSelectedItems(_selectedItems)
  }

  const _setVisibleList = (id: number) => {
    let _visibleList = _.clone(visibleList)
    if (_.includes(_visibleList, id)) {
      _.pull(_visibleList, id)
    } else {
      _visibleList.push(id)
    }
    setVisibleList(_visibleList)
  }

  const propetyComponent = (id: number) => {
    if (id === data[0].id) return <TenkantenPropety canvasId={canvasId} />
    if (id === data[1].id) return <PriceVolumePropety canvasId={canvasId} />
    if (id === data[2].id) return <CycleAnaPropety canvasId={canvasId} />
  }
  // ボタン側からの変更を検知。
  useEffect(() => {
    let _selectedItems: Array<number> = []
    _.forEach(tools, items => {
      if (_.includes([data[0].id, data[1].id, data[2].id], items.id))
        _selectedItems.push(items.id)
    })
    setSelectedItems(_.uniq(_selectedItems))
  }, [tools])

  // マウント時に選ばれている項目にチェックを追加。
  useEffect(() => {
    let _selectedItems: Array<number> = []
    _.forEach(tools, items => {
      if (_.includes([data[0].id, data[1].id, data[2].id], items.id))
        _selectedItems.push(items.id)
    })
    setSelectedItems(_.uniq(_selectedItems))
  }, [])

  return (
    <>
      <div className="chart-setting-category category-tool">
        <div className="chart-setting-category-title">ツール</div>
        {_.map(data, (items, index) => {
          return (
            <div key={items.id} className="chart-setting-item">
              <div
                className="chart-setting-header"
                onMouseOut={() => {
                  if (!_.isNull(tooltipVisible)) setTooltipVisible(null)
                }}
              >
                <label className="checkbox-control">
                  <input
                    type="checkbox"
                    value={items.id}
                    checked={_.includes(selectedItems, items.id)}
                    onChange={() => setSelect(items.id)}
                    disabled={PandFFlag}
                  />
                  <span />
                </label>
                <Btn
                  onClick={() => setSelect(items.id)}
                  name={items.displayTitle}
                  className={ClassNames({
                    disabled: PandFFlag,
                  })}
                />
                <div
                  onMouseOver={e => {
                    if (tooltipVisible !== items.id) setTooltipVisible(items.id)
                    _setPosition(e)
                  }}
                  className="help-icons"
                >
                  <i className="material-icons">help_outline</i>
                </div>
                {tooltipVisible === items.id && createToolTip(index)}
                <i
                  className="material-icons"
                  onClick={() => _setVisibleList(items.id)}
                >
                  {_.includes(visibleList, items.id)
                    ? "keyboard_arrow_up"
                    : "keyboard_arrow_down"}
                </i>
              </div>
              {_.includes(visibleList, items.id) && propetyComponent(items.id)}
            </div>
          )
        })}
      </div>
    </>
  )
})

export default ToolsPropety
